import * as Header from '-/Custom/Header';
import * as Placeholder from '-/Custom/Placeholder';
import EntityFilter from '-/Entity/EntityFilter';
import { Link, Stack, useLocalSearchParams, useRouter } from 'expo-router';
import { useQuery } from 'urql';
import BadgeMask from '@/components/Badge/BadgeMask';
import Avatar from '@/components/Custom/Avatar';
import ActionButton from '@/components/UI/ActionButton';
import Box from '@/components/UI/Box';
import Item from '@/components/UI/PressableRow';
import { type FragmentOf, graphql, readFragment } from '@/gql/graphql';
import BadgeAssigneeList from './components/BadgeAssigneeList';
import BadgeAssigneeListItem, {
  BadgeAssigneeListItemFragment,
} from './components/BadgeAssigneeListItem';

const GetBadgeAssigneeHome = graphql(
  /* GraphQL */ `
    query GetBadgeAssigneeHome(
      $badgeId: uuid!
      $teamId: uuid
      $eventId: uuid
      $personId: String
    ) {
      badge_by_pk(id: $badgeId) {
        id
        title
        color
        can_assign(
          args: { person_id: $personId, event_id: $eventId, team_id: $teamId }
        )
      }
    }
  `,
);

export default function BadgeAssigneeHome() {
  const { push } = useRouter();

  const params = useLocalSearchParams<{
    badgeId?: string;
    personId?: string;
    eventId?: string;
    teamId?: string;
  }>();

  const { badgeId, personId, eventId, teamId } = params;

  const [{ data }] = useQuery({
    query: GetBadgeAssigneeHome,
    variables: {
      badgeId: badgeId!,
      eventId: eventId,
      personId,
      teamId: teamId,
    },
  });

  const badge = data?.badge_by_pk;

  return (
    <>
      <Stack.Screen options={{ headerTitle: 'Badge Assignments' }} />
      <EntityFilter />
      <Header.Toolbar>
        <Header.Row>
          <BadgeMask size="xl" color={badge?.color}>
            <Avatar
              ring={false}
              size="xl"
              color={badge?.color || ''}
              name={badge?.title || ''}
            />
          </BadgeMask>
          <Box direction="vertical" space="xs">
            <Header.Title>{badge?.title}</Header.Title>
            {badge?.can_assign ? (
              <Link
                href={{
                  pathname: '/badge-assignee/create',
                  params: {
                    badgeId,
                    personId,
                    teamId,
                    eventId,
                  },
                }}
                asChild
              >
                <ActionButton
                  variant="success"
                  icon="add-outline"
                  label="Assign Badge"
                />
              </Link>
            ) : null}
          </Box>
        </Header.Row>
      </Header.Toolbar>
      <Header.ColorBar backgroundColor={badge?.color || ''} />
      <BadgeAssigneeList
        search={false}
        badgeAssigneeWhere={{
          ...(badgeId ? { badge_id: { _eq: badgeId } } : {}),
          ...(personId ? { person_id: { _eq: personId } } : {}),
          ...(eventId ? { event_id: { _eq: eventId } } : {}),
          ...(teamId ? { team_id: { _eq: teamId } } : {}),
        }}
        renderItem={({
          item,
        }: {
          item: FragmentOf<typeof BadgeAssigneeListItemFragment>;
        }) => {
          const badgeAssignee = readFragment(
            BadgeAssigneeListItemFragment,
            item,
          );

          return (
            <Item
              onPress={() =>
                push({
                  pathname: '/badge-assignee/[assigneeId]/edit',
                  params: {
                    assigneeId: badgeAssignee.id,
                    teamId: badgeAssignee.team_id,
                    eventId: badgeAssignee.event_id,
                    personId: badgeAssignee.person_id,
                  },
                })
              }
            >
              <BadgeAssigneeListItem fragment={item} />
            </Item>
          );
        }}
        ListEmptyComponent={
          <Placeholder.Container>
            <Placeholder.Text>No Assignments Yet</Placeholder.Text>
          </Placeholder.Container>
        }
      />
    </>
  );
}
